import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import {
  Grid,
  Typography,
  Button,
  TextField,
  FormControl,
  FormLabel,
  Box
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useGlobalSettingState, useComponentWebSocket } from './GlobalSettingUtils';

const GlobalSettingFiles = forwardRef((props, ref) => {
  const { 
    isModified, 
    setIsModified, 
    t,
    openSnackbar
  } = useGlobalSettingState();
  // Add your format specific state here
  const [formatConfig, setFormatConfig] = useState({
    ignoreFilesOlderThanMinutes: 0
  });
  
  // Define handlers for WebSocket messages
  const commandHandlers = useCallback({
    'update_configuration': (message) => {
      if (message.data === 'success') {
        openSnackbar(t('global_setting.save_completed'), 3000);
        setIsModified(false);
      }
    },    'configuration_data': (message) => {
      setFormatConfig(prevConfig => ({
        ignoreFilesOlderThanMinutes: 0,
        ...prevConfig,
        ...(message.data || {})
      }));
    },
    'format_settings': (message) => {
      setFormatConfig(prevConfig => ({
        ignoreFilesOlderThanMinutes: 0,
        ...prevConfig,
        ...(message.data || {})
      }));
    }
  }, [openSnackbar, t, setIsModified]);
  
  // Use our custom WebSocket hook
  const { sendCommand } = useComponentWebSocket(commandHandlers);
  
  // Expose getFormatSettings method to parent component
  useImperativeHandle(ref, () => ({
    getFormatSettings: () => {
      return formatConfig;
    }
  }));
  // Load settings when component mounts
  useEffect(() => {
    sendCommand('get_format_settings');
  }, [sendCommand]);

  const handleIgnoreFilesOlderThanChange = (event) => {
    const value = parseInt(event.target.value) || 0;
    setFormatConfig(prevConfig => ({
      ...prevConfig,
      ignoreFilesOlderThanMinutes: value
    }));
    setIsModified(true);
  };

  const handleSave = () => {
    if (isModified) {
      // Send only format settings to the server
      sendCommand('update_configuration', formatConfig);
      
      console.log('Format settings sent:', formatConfig);
    } else {
      console.error('No changes were made.');
    }
  };  return (
    <Box sx={{ maxWidth: '100%', width: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#5757b0' }}>
        {t('global_setting.format_section', 'File Processing Settings')}
      </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {t('global_setting.format_description', 'Configure how files are processed and handled in the system')}
      </Typography>      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={8} lg={6}>
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1, fontWeight: 'bold', color: '#5757b0', textAlign: 'center' }}>
              {t('global_setting.ignore_files_older_than', 'Ignore Files Older Than (Minutes)')}
            </FormLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <TextField
                type="number"
                value={formatConfig.ignoreFilesOlderThanMinutes || 0}
                onChange={handleIgnoreFilesOlderThanChange}
                inputProps={{ 
                  min: 0,
                  step: 1
                }}
                variant="outlined"
                size="small"
                sx={{
                  width: '150px',
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#5757b0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5757b0',
                    },
                  },
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {t('global_setting.ignore_files_help', 'minutes (0 = never ignore)')}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center', display: 'block' }}>
              {t('global_setting.ignore_files_description', 'Files older than this value will be ignored during processing. Set to 0 to never ignore files based on age.')}
            </Typography>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!isModified}
          sx={{
            backgroundColor: '#5757b0',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#aaaaca',
            },
            py: 1,
            px: 3,
            borderRadius: 2,
          }}
        >
          {t('global_setting.save_button') || 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
});

export { GlobalSettingFiles };
